import React from 'react';
import { userData } from '../../data';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const Projects = ({ onNavigate }) => {
    useDocumentTitle('Projects | Jerel Layog');

    return (
    <section className="content-pane w-full h-full flex flex-col pt-16 md:pt-32 2xl:pt-48 overflow-y-auto overflow-x-hidden custom-scrollbar px-2 sm:px-4 2xl:px-6 relative z-20" tabIndex={-1} aria-labelledby="heading-projects">
        <h2 id="heading-projects" className="text-4xl sm:text-5xl md:text-7xl 2xl:text-8xl font-bebas text-white bg-black px-4 2xl:px-6 py-2 2xl:py-3 border-4 md:border-[6px] 2xl:border-[8px] border-white shadow-[6px_6px_0_#000] md:shadow-[12px_12px_0_#000] 2xl:shadow-[18px_18px_0_#000] mb-8 md:mb-12 2xl:mb-16 transform md:-rotate-3 2xl:-rotate-4 relative z-20 inline-block w-fit ml-2 2xl:ml-4">
            PROJECTS
        </h2>

        <div className="flex flex-col gap-24 md:gap-32 2xl:gap-40 max-w-7xl 2xl:max-w-[90rem] pb-12 md:pb-20 2xl:pb-28 px-4 sm:px-8 md:px-12 2xl:px-16 pt-12 md:pt-20 2xl:pt-28">
            {userData.projects.slice(0, 3).map((proj, idx) => (
                <article
                    key={idx}
                    onClick={() => onNavigate('PROJECT_DETAIL', proj)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onNavigate('PROJECT_DETAIL', proj);
                        }
                    }}
                    className="relative group cursor-pointer focus-within:-translate-y-2 transition-transform w-full flex flex-col items-center"
                    tabIndex={0}
                    role="button"
                    aria-label={`View project details: ${proj.name}`}
                >

                    <div className="relative w-full">
                        <div className="absolute inset-0 bg-black transform translate-x-2 translate-y-2 md:translate-x-4 md:translate-y-4 2xl:translate-x-6 2xl:translate-y-6" aria-hidden="true"></div>

                        {/* Main Card Container */}
                        <div className="relative border-4 md:border-[4px] 2xl:border-[6px] border-black transform group-hover:-translate-y-2 group-hover:-translate-x-2 2xl:group-hover:-translate-y-3 2xl:group-hover:-translate-x-3 transition-transform duration-200 w-full flex flex-col overflow-hidden bg-black min-h-[16rem] md:min-h-[24rem] 2xl:min-h-[32rem]">
                            {/* Full Background Image */}
                            {proj.image && (
                                <div className="absolute inset-0 z-0">
                                    <img
                                        src={proj.image}
                                        alt={proj.name}
                                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-all duration-500 ease-out z-0"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Title Box */}
                        <div className="absolute -top-10 -right-4 sm:-top-12 sm:-right-8 md:-top-16 md:-right-12 2xl:-top-20 2xl:-right-16 bg-[#E5E5E5] text-black px-4 py-2 sm:px-6 sm:py-3 2xl:px-8 2xl:py-4 border-4 md:border-[6px] 2xl:border-[8px] border-black z-30 group-hover:bg-[#FFF200] transition-colors max-w-[85%] sm:max-w-[70%] 2xl:max-w-[60%]">
                            <h3 className="text-lg sm:text-xl md:text-3xl lg:text-4xl 2xl:text-5xl font-black italic uppercase tracking-tight">{proj.name}</h3>
                        </div>
                    </div>

                    {/* Description Box */}
                    <div className="relative -mt-6 sm:-mt-8 md:-mt-10 2xl:-mt-14 bg-[#F5F5F5] text-black px-4 py-3 sm:px-6 sm:py-4 2xl:px-8 2xl:py-6 border-4 md:border-[6px] 2xl:border-[8px] border-black shadow-[4px_4px_0_#000] md:shadow-[6px_6px_0_#000] 2xl:shadow-[10px_10px_0_#000] z-30 group-hover:bg-white transition-colors w-[95%] sm:w-[90%] lg:w-[85%] 2xl:w-[80%] flex flex-col gap-3 2xl:gap-5">
                        <p className="font-montserrat text-xs sm:text-sm md:text-base 2xl:text-lg font-bold leading-snug 2xl:leading-relaxed">{proj.desc}</p>
                        <div className="flex flex-wrap gap-1.5 md:gap-2 2xl:gap-3" aria-label="Technologies used">
                            {proj.tags.map(tag => (
                                <span key={tag} className="text-[10px] sm:text-xs md:text-sm 2xl:text-base font-bold bg-[#A30000] text-white px-2 py-1 2xl:px-3 2xl:py-1.5 transform md:-rotate-2 2xl:-rotate-3 border-2 2xl:border-[3px] border-black group-hover:bg-[#FFF200] group-hover:text-black transition-colors shadow-[2px_2px_0_#000] 2xl:shadow-[4px_4px_0_#000]">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>

                </article>
            ))}
        </div>

        <div className="mt-8 2xl:mt-12 mb-24 md:mb-32 2xl:mb-40 px-2 2xl:px-4 w-full max-w-5xl 2xl:max-w-6xl flex justify-center md:justify-start relative z-20">
            <button
                onClick={() => onNavigate("ARCHIVE")}
                className="w-full sm:w-auto text-center bg-[#FFF200] text-black font-bebas text-3xl md:text-4xl 2xl:text-5xl px-8 py-3 md:px-12 md:py-4 2xl:px-16 2xl:py-5 border-2 md:border-4 2xl:border-[6px] border-black transform md:hover:scale-105 2xl:hover:scale-110 hover:bg-black hover:text-[#FFF200] hover:border-[#FFF200] transition-all md:-rotate-2 2xl:-rotate-3 shadow-[4px_4px_0_#000] md:shadow-[8px_8px_0_#000] 2xl:shadow-[12px_12px_0_#000] focus:outline-none focus-visible:ring-4 focus-visible:ring-white"
            >
                VIEW FULL PROJECT ARCHIVE
            </button>
        </div>
    </section>
    );
};

export default Projects;